import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { logActivity } from "@/lib/activity";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { StatusBadge, PageHeader, formatTime, formatDate } from "@/lib/ui";

const APPT_TYPES = ["Initial consultation", "Intake", "Follow-up", "Assessment", "Meeting", "Other"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function startOfWeek(d) { const n = new Date(d); const day = n.getDay(); n.setDate(n.getDate() - day); n.setHours(0,0,0,0); return n; }

export default function Calendar() {
  const { currentOrgId, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("month"); // month|week|day|list
  const [cursor, setCursor] = useState(new Date());
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm(params.get("client_id")));
  const [saving, setSaving] = useState(false);

  function defaultForm(clientId) {
    const t = new Date(); t.setMinutes(0); t.setSeconds(0);
    const end = new Date(t.getTime() + 60 * 60 * 1000);
    return {
      title: "", client_id: clientId || "none", appointment_type: "Meeting",
      starts_at: iso(t), ends_at: iso(end), location: "", meeting_link: "", notes: "", status: "scheduled",
      recurrence: "none", recurrence_count: 4, reminder_minutes_before: null,
    };
  }
  function iso(d) { const t = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return t.toISOString().slice(0, 16); }

  const load = async () => {
    if (!currentOrgId) return;
    setLoading(true);
    const [a, c] = await Promise.all([
      supabase.from("appointments").select("*,clients(id,first_name,last_name)").eq("organization_id", currentOrgId).order("starts_at", { ascending: true }),
      supabase.from("clients").select("id,first_name,last_name").eq("organization_id", currentOrgId).order("first_name"),
    ]);
    setRows(a.data || []); setClients(c.data || []); setLoading(false);
  };
  useEffect(() => { load(); }, [currentOrgId]);
  useEffect(() => {
    if (params.get("new") === "1") { setOpen(true); params.delete("new"); setParams(params, { replace: true }); }
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Appointment title is required");
    if (new Date(form.ends_at) <= new Date(form.starts_at)) return toast.error("End time must be after start time");
    setSaving(true);
    const payload = {
      organization_id: currentOrgId,
      title: form.title.trim(),
      appointment_type: form.appointment_type,
      client_id: form.client_id === "none" ? null : form.client_id || null,
      starts_at: new Date(form.starts_at).toISOString(),
      ends_at: new Date(form.ends_at).toISOString(),
      location: form.location || null,
      meeting_link: form.meeting_link || null,
      notes: form.notes || null,
      status: form.status,
      reminder_minutes_before: form.reminder_minutes_before,
      created_by: user.id,
    };
    let result;
    if (editId) {
      result = await supabase.from("appointments").update(payload).eq("id", editId).select().single();
    } else if (form.recurrence !== "none") {
      // Recurring — use backend
      try {
        const resp = await apiFetch("/appointments/recurring", {
          method: "POST",
          body: JSON.stringify({
            organization_id: currentOrgId,
            title: form.title.trim(),
            client_id: form.client_id === "none" ? null : form.client_id,
            appointment_type: form.appointment_type,
            starts_at: new Date(form.starts_at).toISOString(),
            ends_at: new Date(form.ends_at).toISOString(),
            location: form.location || null,
            meeting_link: form.meeting_link || null,
            notes: form.notes || null,
            status: form.status,
            frequency: form.recurrence,
            count: Number(form.recurrence_count) || 4,
          }),
        });
        result = { data: (resp.appointments || [])[0], error: null };
        toast.success(`${resp.count} appointments scheduled`);
      } catch (e) {
        setSaving(false);
        return toast.error(e.message);
      }
    } else {
      result = await supabase.from("appointments").insert(payload).select().single();
    }
    setSaving(false);
    if (result.error) return toast.error(result.error.message);
    if (form.recurrence === "none" || editId) toast.success(editId ? "Appointment updated" : "Appointment scheduled");
    // Sync to connected calendars (Google/Outlook of the assigned staff). Best-effort — never blocks the UI.
    if (result.data?.id) {
      apiFetch("/integrations/calendar/sync-appointment", {
        method: "POST", body: JSON.stringify({ appointment_id: result.data.id }),
      }).catch(() => {});
    }
    setForm(defaultForm()); setEditId(null); setOpen(false);
    load();
    if (!editId && result.data) logActivity({ organization_id: currentOrgId, actor_user_id: user.id, client_id: result.data.client_id,
      action: "appointment.created", summary: `scheduled "${result.data.title}"`,
      entity_type: "appointments", entity_id: result.data.id });
  };

  const openAt = (date, hour = null) => {
    const start = new Date(date);
    if (hour !== null) start.setHours(hour, 0, 0, 0);
    else { const now = new Date(); start.setHours(now.getHours() + 1, 0, 0, 0); }
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    setEditId(null);
    setForm({ ...defaultForm(params.get("client_id")), starts_at: iso(start), ends_at: iso(end) });
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditId(a.id);
    setForm({
      title: a.title, client_id: a.client_id || "none",
      appointment_type: a.appointment_type,
      starts_at: iso(new Date(a.starts_at)),
      ends_at: iso(new Date(a.ends_at)),
      location: a.location || "", meeting_link: a.meeting_link || "",
      notes: a.notes || "", status: a.status,
      recurrence: "none", recurrence_count: 4,
      reminder_minutes_before: a.reminder_minutes_before ?? null,
    });
    setOpen(true);
  };

  // Deep-link: open ?id=xxx appointment automatically once appointments load
  useEffect(() => {
    const id = params.get("id");
    if (!id || !rows?.length || open) return;
    const row = rows.find((r) => r.id === id);
    if (row) { openEdit(row); params.delete("id"); setParams(params, { replace: true }); }
    /* eslint-disable-next-line */
  }, [rows, params.get("id")]);

  const cancelAppt = async () => {
    if (!editId) return;
    if (!window.confirm("Cancel this appointment?")) return;
    await supabase.from("appointments").update({ status: "cancelled" }).eq("id", editId);
    // Sync cancellation to connected calendar (delete event)
    apiFetch("/integrations/calendar/sync-appointment", {
      method: "POST", body: JSON.stringify({ appointment_id: editId }),
    }).catch(() => {});
    toast.success("Appointment cancelled");
    setOpen(false); setEditId(null); load();
  };

  // Nav
  const shift = (dir) => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else if (view === "day") d.setDate(d.getDate() + dir);
    setCursor(d);
  };
  const goToday = () => setCursor(new Date());

  const monthGrid = useMemo(() => {
    if (view !== "month") return [];
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const start = new Date(first); start.setDate(1 - first.getDay());
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      const appts = rows.filter((a) => new Date(a.starts_at).toDateString() === d.toDateString());
      days.push({ d, inMonth: d.getMonth() === first.getMonth(), appts });
    }
    return days;
  }, [view, cursor, rows]);

  const listRows = useMemo(() => {
    if (view === "list") return rows;
    if (view === "day") return rows.filter((a) => new Date(a.starts_at).toDateString() === cursor.toDateString());
    if (view === "week") {
      const ws = startOfWeek(cursor); const we = new Date(ws); we.setDate(we.getDate() + 7);
      return rows.filter((a) => { const d = new Date(a.starts_at); return d >= ws && d < we; });
    }
    return [];
  }, [view, cursor, rows]);

  const title = useMemo(() => {
    if (view === "month") return cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    if (view === "week") { const ws = startOfWeek(cursor); const we = new Date(ws); we.setDate(we.getDate() + 6); return `${ws.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${we.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`; }
    if (view === "day") return cursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    return "All appointments";
  }, [view, cursor]);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="calendar-page">
      <PageHeader title="Calendar" subtitle="See what's scheduled, at a glance."
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(defaultForm()); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-appt-btn" className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Plus size={16} className="mr-1.5" /> New appointment</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg">
              <DialogHeader><DialogTitle className="font-display tracking-tight">{editId ? "Edit appointment" : "New appointment"}</DialogTitle></DialogHeader>
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                <div><Label>Title *</Label><Input data-testid="appt-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1.5" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Client</Label>
                    <Select value={form.client_id} onValueChange={(v) => setForm({...form, client_id: v})}>
                      <SelectTrigger data-testid="appt-client" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={form.appointment_type} onValueChange={(v) => setForm({...form, appointment_type: v})}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {APPT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Start *</Label><Input type="datetime-local" data-testid="appt-start" value={form.starts_at} onChange={(e) => setForm({...form, starts_at: e.target.value})} className="mt-1.5" /></div>
                  <div><Label>End *</Label><Input type="datetime-local" data-testid="appt-end" value={form.ends_at} onChange={(e) => setForm({...form, ends_at: e.target.value})} className="mt-1.5" /></div>
                </div>
                  <div>
                    <Label>Location</Label>
                    <Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="mt-1.5" placeholder="Office, phone, video…" />
                  </div>
                  <div>
                    <Label>Reminder</Label>
                    <Select value={form.reminder_minutes_before == null ? "none" : String(form.reminder_minutes_before)} onValueChange={(v) => setForm({...form, reminder_minutes_before: v === "none" ? null : Number(v)})}>
                      <SelectTrigger className="mt-1.5" data-testid="appt-reminder"><SelectValue placeholder="No reminder" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No reminder</SelectItem>
                        <SelectItem value="15">15 minutes before</SelectItem>
                        <SelectItem value="30">30 minutes before</SelectItem>
                        <SelectItem value="60">1 hour before</SelectItem>
                        <SelectItem value="1440">1 day before</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                <div><Label>Meeting link</Label><Input value={form.meeting_link} onChange={(e) => setForm({...form, meeting_link: e.target.value})} className="mt-1.5" placeholder="https://…" /></div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="no_show">No show</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1.5" /></div>
                {!editId && (
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#E8E2D9]">
                    <div>
                      <Label>Repeat</Label>
                      <Select value={form.recurrence} onValueChange={(v) => setForm({...form, recurrence: v})}>
                        <SelectTrigger className="mt-1.5" data-testid="appt-recurrence"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Does not repeat</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {form.recurrence !== "none" && (
                      <div>
                        <Label>Number of occurrences</Label>
                        <Input type="number" min="2" max="52" value={form.recurrence_count} onChange={(e) => setForm({...form, recurrence_count: e.target.value})} className="mt-1.5" data-testid="appt-recurrence-count" />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2">
                {editId && <Button onClick={cancelAppt} variant="outline" className="rounded-full text-red-600 border-red-200 mr-auto">Cancel appointment</Button>}
                <Button onClick={() => setOpen(false)} variant="outline" className="rounded-full">Close</Button>
                <Button data-testid="save-appt-btn" onClick={save} disabled={saving} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">{saving ? "Saving…" : "Save"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex items-center gap-3 flex-wrap justify-between bg-white rounded-2xl border border-[#E8E2D9] p-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shift(-1)} className="rounded-full h-9 w-9 p-0"><ChevronLeft size={16} /></Button>
          <Button variant="outline" size="sm" onClick={goToday} className="rounded-full">Today</Button>
          <Button variant="outline" size="sm" onClick={() => shift(1)} className="rounded-full h-9 w-9 p-0"><ChevronRight size={16} /></Button>
          <div className="text-sm font-medium ml-2" data-testid="cal-title">{title}</div>
        </div>
        <div className="flex items-center gap-1 bg-[#FAF8F5] rounded-full p-1">
          {["month", "week", "day", "list"].map((v) => (
            <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${view === v ? "bg-[#E8712C] text-white" : "text-[#57534E]"}`}>{v}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : view === "month" ? (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
          <div className="grid grid-cols-7 border-b border-[#E8E2D9] text-xs font-semibold text-[#57534E] uppercase tracking-widest">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="p-3 text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {monthGrid.map(({ d, inMonth, appts }) => {
              const isToday = d.toDateString() === new Date().toDateString();
              const dateKey = d.toISOString().slice(0,10);
              return (
                <div key={dateKey} onClick={() => openAt(d)} data-testid={`month-cell-${dateKey}`}
                  className={`min-h-[110px] border-r border-b border-[#E8E2D9] p-2 cursor-pointer hover:bg-[#FDE9D8]/40 transition-colors ${!inMonth ? "bg-[#FAF8F5]" : ""} ${isToday ? "ring-2 ring-inset ring-[#E8712C]/40" : ""}`}>
                  <div className={`text-xs mb-1 ${isToday ? "text-[#E8712C] font-bold" : inMonth ? "text-[#57534E]" : "text-[#A8A29E]"}`}>{d.getDate()}</div>
                  <div className="space-y-1">
                    {appts.slice(0, 3).map((a) => (
                      <button key={a.id} onClick={(e) => { e.stopPropagation(); openEdit(a); }} data-testid={`month-appt-${a.id}`}
                        className={`w-full text-left text-[10px] px-1.5 py-0.5 rounded truncate hover:opacity-80 ${a.status === "cancelled" ? "bg-[#F0E0E0] text-[#7A2323] line-through" : a.status === "completed" ? "bg-[#DCF2E1] text-[#1F6B36]" : a.status === "confirmed" ? "bg-[#DCEEF7] text-[#1F4A6B]" : "bg-[#FDE9D8] text-[#B85C1F]"}`}>
                        {formatTime(a.starts_at)} {a.title}
                      </button>
                    ))}
                    {appts.length > 3 && <div className="text-[10px] text-[#A8A29E]">+{appts.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : view === "week" ? (
        (() => {
          const ws = startOfWeek(cursor);
          const days = Array.from({length:7},(_,i)=>{const d=new Date(ws); d.setDate(d.getDate()+i); return d;});
          const hours = Array.from({length:14},(_,i)=>i+7); // 7am-8pm
          const byDayHour = {};
          rows.forEach((a) => {
            const s = new Date(a.starts_at);
            if (s >= ws && s < new Date(ws.getTime()+7*86400000)) {
              const k = `${s.getDay()}-${s.getHours()}`;
              (byDayHour[k] = byDayHour[k] || []).push(a);
            }
          });
          return (
            <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-x-auto">
              <div className="grid grid-cols-[60px_repeat(7,minmax(120px,1fr))] border-b border-[#E8E2D9] text-xs font-semibold text-[#57534E]">
                <div />
                {days.map((d) => (
                  <div key={d.toISOString().slice(0,10)} className={`p-2 text-center border-l border-[#E8E2D9] ${d.toDateString()===new Date().toDateString()?"text-[#E8712C]":""}`}>
                    <div className="uppercase tracking-widest">{d.toLocaleDateString(undefined,{weekday:"short"})}</div>
                    <div className="text-lg font-display font-bold">{d.getDate()}</div>
                  </div>
                ))}
              </div>
              <div>
                {hours.map((h) => (
                  <div key={h} className="grid grid-cols-[60px_repeat(7,minmax(120px,1fr))] border-b border-[#E8E2D9]">
                    <div className="text-[10px] text-[#A8A29E] p-1 text-right">{h}:00</div>
                    {days.map((d, di) => {
                      const list = byDayHour[`${di}-${h}`] || [];
                      const slotKey = `${d.toISOString().slice(0,10)}-${h}`;
                      return (
                        <div key={slotKey} onClick={() => openAt(d, h)} data-testid={`week-slot-${slotKey}`}
                          className="min-h-[52px] border-l border-[#E8E2D9] p-1 cursor-pointer hover:bg-[#FDE9D8]/40">
                          {list.map((a) => (
                            <button key={a.id} onClick={(e)=>{e.stopPropagation(); openEdit(a);}} data-testid={`week-appt-${a.id}`}
                              className={`w-full text-left text-[10px] px-1.5 py-0.5 rounded mb-0.5 truncate ${a.status==="cancelled"?"bg-[#F0E0E0] text-[#7A2323] line-through":a.status==="completed"?"bg-[#DCF2E1] text-[#1F6B36]":a.status==="confirmed"?"bg-[#DCEEF7] text-[#1F4A6B]":"bg-[#FDE9D8] text-[#B85C1F]"}`}>
                              {formatTime(a.starts_at)} {a.title}
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          );
        })()
      ) : view === "day" ? (
        (() => {
          const hours = Array.from({length:14},(_,i)=>i+7);
          const dayList = rows.filter((a) => new Date(a.starts_at).toDateString() === cursor.toDateString());
          const byHour = {};
          dayList.forEach((a) => { const h = new Date(a.starts_at).getHours(); (byHour[h] = byHour[h] || []).push(a); });
          return (
            <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
              <div className="px-6 py-3 border-b border-[#E8E2D9] font-display font-semibold">{cursor.toLocaleDateString(undefined,{weekday:"long",month:"long",day:"numeric"})}</div>
              {hours.map((h) => (
                <div key={h} className="grid grid-cols-[80px_1fr] border-b border-[#E8E2D9] min-h-[64px]">
                  <div className="text-xs text-[#A8A29E] p-2">{h}:00</div>
                  <div onClick={() => openAt(cursor, h)} data-testid={`day-slot-${h}`} className="p-2 cursor-pointer hover:bg-[#FDE9D8]/40 space-y-1">
                    {(byHour[h] || []).map((a) => (
                      <button key={a.id} onClick={(e)=>{e.stopPropagation(); openEdit(a);}} data-testid={`day-appt-${a.id}`}
                        className={`w-full text-left text-xs px-2 py-1 rounded flex items-center justify-between ${a.status==="cancelled"?"bg-[#F0E0E0] text-[#7A2323] line-through":a.status==="completed"?"bg-[#DCF2E1] text-[#1F6B36]":a.status==="confirmed"?"bg-[#DCEEF7] text-[#1F4A6B]":"bg-[#FDE9D8] text-[#B85C1F]"}`}>
                        <span className="truncate">{formatTime(a.starts_at)} · {a.title}{a.clients?` · ${a.clients.first_name} ${a.clients.last_name}`:""}</span>
                        <StatusBadge value={a.status} />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9]">
          {listRows.length === 0 ? (
            <div className="p-16 text-center">
              <CalendarDays size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#57534E]">No appointments in this view.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E8E2D9]">
              {listRows.map((a) => (
                <button key={a.id} onClick={() => openEdit(a)} data-testid={`appt-row-${a.id}`}
                  className="w-full text-left px-6 py-4 flex items-center gap-4 hover:bg-[#FAF8F5]">
                  <div className="w-28 text-sm">
                    <div className="font-medium text-[#E8712C]">{formatTime(a.starts_at)}</div>
                    <div className="text-xs text-[#A8A29E]">{formatDate(a.starts_at)}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{a.title}</div>
                    <div className="text-xs text-[#57534E]">
                      {a.clients && `${a.clients.first_name} ${a.clients.last_name} · `}{a.appointment_type}
                      {a.location && ` · ${a.location}`}
                    </div>
                  </div>
                  <StatusBadge value={a.status} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
